import { useState } from 'react';
import { format, isSameDay } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { DateRangePicker, type RangeKeyDict } from 'react-date-range';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import '@/components/ui/date-range.css';

interface DateSelection { startDate: Date; endDate: Date }

export function BrandHealthDateFilter({ value, onChange }: {
  value: DateSelection;
  onChange: (range: DateSelection) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const label = isSameDay(value.startDate, value.endDate)
    ? format(value.startDate, 'MMM d, yyyy')
    : `${format(value.startDate, 'MMM d, yyyy')} - ${format(value.endDate, 'MMM d, yyyy')}`;
  function selectRange(ranges: RangeKeyDict): void {
    const range = ranges.selection;
    if (range?.startDate && range.endDate) setDraft({ startDate: range.startDate, endDate: range.endDate });
  }
  return (
    <Popover open={open} onOpenChange={(next) => { if (next) setDraft(value); setOpen(next); }}>
      <PopoverTrigger asChild>
        <button type='button' aria-label='Brand Health date range' className='flex h-8 w-full items-center gap-2 rounded-full border border-gray-300 bg-white px-3 text-xs text-gray-800 focus-visible:outline-2 focus-visible:outline-offset-2'>
          <CalendarIcon aria-hidden='true' className='size-3.5 shrink-0' />
          <span className='min-w-0 flex-1 whitespace-nowrap text-center'>{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align='end' sideOffset={8} collisionPadding={16} className='w-auto max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg p-0'>
        <div className='max-h-[min(480px,calc(var(--radix-popover-content-available-height)-64px))] overflow-auto [&_.rdrDateRangePickerWrapper]:flex-wrap [&_.rdrDefinedRangesWrapper]:shrink-0 [&_.rdrMonth]:max-w-full [&_.rdrStaticRange]:rounded-[4px] max-sm:[&_.rdrDefinedRangesWrapper]:w-full'>
          <DateRangePicker ranges={[{ ...draft, key: 'selection' }]} onChange={selectRange} months={1} direction='horizontal' maxDate={new Date()} moveRangeOnFirstSelection={false} editableDateInputs inputRanges={[]} />
        </div>
          <div className='flex justify-end gap-2 border-t border-gray-200 p-3'>
            <Button type='button' variant='outline' size='sm' onClick={() => setOpen(false)}>Cancel</Button>
            <Button type='button' size='sm' onClick={() => { onChange(draft); setOpen(false); }}>Apply</Button>
          </div>
      </PopoverContent>
    </Popover>
  );
}
